import React from 'react';
import { Translate } from 'react-jhipster';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { driverDisplayName, FormNextTarget, IAssignmentVehicleOption, ICouplingAssignmentRow, IEligibleDriver } from 'app/shared/model/driver-assignment.model';

export const T = 'fleetSenseApp.driverClassification.management';

export const MAX_IMPLEMENT_SLOTS = 6;

export const IMPLEMENT_KEYS = ['implement', 'implement2', 'implement3', 'implement4', 'implement5', 'implement6'] as const;
export type ImplementKey = typeof IMPLEMENT_KEYS[number];

export const EMPTY_CELL = '—';

// "Implemento" is always shown; 2º..6º only when some rig in the whole filter (not just this page) has that many.
export const visibleImplementSlots = (maxImplementCount: number): number => {
  const value = Number.isFinite(maxImplementCount) ? Math.floor(maxImplementCount) : 0;
  return Math.min(MAX_IMPLEMENT_SLOTS, Math.max(1, value));
};

export type DisplayRow = ICouplingAssignmentRow & {
  truckLabel: string;
  implementLabel: string;
  implement2Label: string;
  implement3Label: string;
  implement4Label: string;
  implement5Label: string;
  implement6Label: string;
  validFromLabel: string;
};

// validFrom is a UTC day boundary; the `date` cell type of TableComponent would render it in local time.
export const formatUtcDay = (instant?: string | null): string => {
  if (!instant) {
    return '';
  }
  const [year, month, day] = instant.substring(0, 10).split('-');
  return day && month && year ? `${day}/${month}/${year}` : '';
};

export const toDisplayRow = (row: ICouplingAssignmentRow): DisplayRow => ({
  ...row,
  truckLabel: row.truck || EMPTY_CELL,
  implementLabel: row.implement || EMPTY_CELL,
  implement2Label: row.implement2 || EMPTY_CELL,
  implement3Label: row.implement3 || EMPTY_CELL,
  implement4Label: row.implement4 || EMPTY_CELL,
  implement5Label: row.implement5 || EMPTY_CELL,
  implement6Label: row.implement6 || EMPTY_CELL,
  validFromLabel: formatUtcDay(row.validFrom) || EMPTY_CELL,
});

export interface ColumnRenderers {
  renderDriver: (row: DisplayRow) => React.ReactNode;
  renderActions: (row: DisplayRow) => React.ReactNode;
}

const header = (key: string, contentKey: string, sortable = false): IHeaderItem => ({
  key,
  sortable,
  component: React.createElement(Translate, { contentKey }),
});

// Sortable headers match the backend whitelist (validFrom, truck). Actions have no header: TableComponent adds a
// trailing empty header cell.
export const buildColumns = (slots: number, renderers?: ColumnRenderers): { headers: IHeaderItem[]; data: IListData[] } => {
  const count = visibleImplementSlots(slots);
  const implementKeys = IMPLEMENT_KEYS.slice(0, count);
  const headers: IHeaderItem[] = [
    header('driverName', `${T}.table.driver`),
    header('truck', 'fleetSenseApp.VirtualCoupling.truck', true),
    ...implementKeys.map(key => header(key, `fleetSenseApp.VirtualCoupling.${key}`)),
    header('validFrom', `${T}.table.validFrom`, true),
  ];
  const data: IListData[] = [
    { type: 'child', value: 'driverName', renderTableChild: renderers?.renderDriver },
    { type: 'text', value: 'truckLabel' },
    ...implementKeys.map<IListData>(key => ({ type: 'text', value: `${key}Label` })),
    { type: 'text', value: 'validFromLabel' },
    { type: 'child', value: 'rowKey', renderTableChild: renderers?.renderActions },
  ];
  return { headers, data };
};

export interface NewAssignmentQuery {
  focus?: 'company' | FormNextTarget | null;
  next?: FormNextTarget | null;
  companyId?: number | null;
  companyName?: string | null;
}

// URLSearchParams encodes company names such as "A & B" safely.
export const buildNewAssignmentUrl = (baseUrl: string, query: NewAssignmentQuery = {}): string => {
  const params = new URLSearchParams();
  if (query.focus) {
    params.set('focus', query.focus);
  }
  if (query.next) {
    params.set('next', query.next);
  }
  if (query.companyId != null) {
    params.set('companyId', String(query.companyId));
    if (query.companyName) {
      params.set('companyName', query.companyName);
    }
  }
  const search = params.toString();
  return `${baseUrl}/new${search ? `?${search}` : ''}`;
};

export type SummaryKind = 'truck' | 'driver' | 'implement';

export const SUMMARY_KINDS: SummaryKind[] = ['truck', 'driver', 'implement'];

export const SUMMARY_KEY = { truck: 'trucks', driver: 'drivers', implement: 'implements' } as const;

export interface CardFilter {
  isAdmin: boolean;
  companyId?: number | null;
  companyName?: string | null;
}

// ADMIN without a company filter sees global totals and must pick the company before any vehicle/driver can be listed.
export const buildCardUrl = (baseUrl: string, kind: SummaryKind, { isAdmin, companyId, companyName }: CardFilter): string => {
  if (isAdmin && companyId == null) {
    return buildNewAssignmentUrl(baseUrl, { focus: 'company', next: kind });
  }
  return buildNewAssignmentUrl(baseUrl, isAdmin ? { focus: kind, companyId, companyName } : { focus: kind });
};

export interface ChipSpec {
  tone: 'neutral' | 'warning';
  labelKey: string;
  params?: Record<string, string>;
}

const sameCoupling = (current?: number | null, target?: number | null): boolean => current != null && target != null && current === target;

// Only the injected empty option (no plate) has no chip: a slot prefilled from a row has id null but keeps its
// plate and its coupling metadata, so it must still read as "move from".
export const resolveVehicleChip = (
  kind: 'TRUCK' | 'IMPLEMENT',
  option: IAssignmentVehicleOption | null | undefined,
  targetCouplingId: number | null | undefined
): ChipSpec | null => {
  if (!option || option.licensePlate == null || option.currentCouplingId == null) {
    return null;
  }
  if (sameCoupling(option.currentCouplingId, targetCouplingId)) {
    return { tone: 'neutral', labelKey: 'chip.current' };
  }
  if (kind === 'TRUCK') {
    return option.currentDriverName
      ? { tone: 'warning', labelKey: 'chip.assignedTo', params: { driver: option.currentDriverName } }
      : { tone: 'warning', labelKey: 'chip.hasCoupling' };
  }
  return { tone: 'warning', labelKey: 'chip.moveFrom', params: { truck: option.currentCouplingTruck ?? '' } };
};

export const resolveDriverChip = (driver: IEligibleDriver | null | undefined, targetCouplingId: number | null | undefined): ChipSpec | null => {
  if (!driver) {
    return null;
  }
  if (sameCoupling(driver.currentCouplingId, targetCouplingId)) {
    return { tone: 'neutral', labelKey: 'chip.current' };
  }
  if (driver.currentTruck != null) {
    return { tone: 'warning', labelKey: 'chip.moveFrom', params: { truck: driver.currentTruck } };
  }
  return null;
};

// Text of a vehicle option in the selector menu; a missing/blank plate reads as the list's empty cell, never blank.
export const vehicleOptionText = (option?: IAssignmentVehicleOption | null): string => option?.licensePlate?.trim() || EMPTY_CELL;

export const compactPlates = (list: Array<IAssignmentVehicleOption | null | undefined>): string[] =>
  list.reduce<string[]>((plates, item) => (item?.licensePlate ? [...plates, item.licensePlate] : plates), []);

export interface MoveSelection {
  implements: Array<IAssignmentVehicleOption | null | undefined>;
  driver: IEligibleDriver | null | undefined;
}

export interface PreviousDriver {
  userId?: number | null;
  driverName?: string | null;
  truck?: string | null;
}

export interface PendingMove {
  key: 'moves.implement' | 'moves.driver' | 'moves.replacedDriver';
  params: Record<string, string>;
}

// Everything the save will take away from another rig (or from the target's current driver), for the confirm dialog.
export const pendingMoves = (selection: MoveSelection, targetCouplingId: number | null | undefined, prevDriver?: PreviousDriver | null): PendingMove[] => {
  const moves: PendingMove[] = [];
  selection.implements.forEach(item => {
    if (item?.licensePlate && item.currentCouplingId != null && !sameCoupling(item.currentCouplingId, targetCouplingId)) {
      moves.push({ key: 'moves.implement', params: { plate: item.licensePlate, truck: item.currentCouplingTruck ?? '' } });
    }
  });
  const driver = selection.driver;
  if (driver && driver.currentTruck != null && !sameCoupling(driver.currentCouplingId, targetCouplingId)) {
    moves.push({ key: 'moves.driver', params: { driver: driverDisplayName(driver), truck: driver.currentTruck } });
  }
  if (prevDriver?.userId != null && prevDriver.userId !== (driver?.id ?? null)) {
    moves.push({ key: 'moves.replacedDriver', params: { driver: prevDriver.driverName ?? '', truck: prevDriver.truck ?? '' } });
  }
  return moves;
};

// Free drivers first (they are the ones counted by the upsell card), then by name.
export const sortDrivers = (drivers: IEligibleDriver[]): IEligibleDriver[] =>
  [...drivers].sort((a, b) => {
    const busyA = a.currentTruck != null ? 1 : 0;
    const busyB = b.currentTruck != null ? 1 : 0;
    return busyA - busyB || driverDisplayName(a).localeCompare(driverDisplayName(b));
  });

// The backend answers problem+json with errorKey, or with message 'error.<errorKey>'.
export const extractErrorKey = (error: any): string | null => {
  const data = error?.response?.data;
  if (data?.errorKey) {
    return data.errorKey;
  }
  const message: unknown = data?.message;
  return typeof message === 'string' && message.startsWith('error.') ? message.substring('error.'.length) : null;
};
